import React, { useCallback, useEffect, useState, useRef, useContext, useMemo } from 'react';
import { Checkbox } from '@material-ui/core';
import { Typography, Box, useMediaQuery, useTheme } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { MTable, RepealableName } from '../../../../../Components';
import { HtmlView } from '../../../../../Components/Html/Html.View';
import { OwnNotes } from '../../../../../Components/OwnNotes';
import { useFilter } from '../../../../../Components/ListFilter';
import { AptorApi, IGridQueryRequest, OrderDirection } from '../../../../../Api/AptorApi';
import { useAptorApi } from '../../../../../Api';
import { UserContext } from '../../../../../Context/UserContext/UserContext';
import { Query } from '@material-table/core';
import { StepProps } from './types';
import moment from 'moment';
import { StepButtonBar } from '../OtherRequirementWizard/StepButtonBar';
import { RequirementsListFilter } from './CustomerRequirements.List.Filter';

interface Step1LawsTableWithFilterProps extends StepProps {
  onCompanyUnitSelect?: (unit: { id: number; name: string }) => void;
  connectionAddedToLaws: Array<{ id: number; name: string; number: string }>;
  lawHasConnection: (requirementId: number) => boolean;
  onLawCheckboxChange: (requirement: { id: number; name: string; number: string }, isSelected: boolean) => void;
  currentStepName: string;
  updateConnectionInfo: any;
  setUpdateConnectionInfo: (info: any) => void;
}

const Step1LawsTableWithFilter = ({
  onNext,
  onBack,
  onCancel,
  isFirstStep,
  onCompanyUnitSelect,
  connectionAddedToLaws,
  lawHasConnection,
  onLawCheckboxChange,
  currentStepName,
  updateConnectionInfo,
  setUpdateConnectionInfo,
}: Step1LawsTableWithFilterProps) => {
  const { api } = useAptorApi();
  const { formatMessage } = useIntl();
  const { companyUnits } = useContext(UserContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filters, setFilters] = useFilter();
  const tableRef = useRef<any>(null);
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false);
  const selectedRequirementIdRef = useRef<number | null>(null);
  const [selectionVersion, setSelectionVersion] = useState(0);

  const selectedRequirementRef = useRef<any | null>(null);

  // Restore selection when returning from next step
  useEffect(() => {
    if (connectionAddedToLaws && connectionAddedToLaws.length > 0) {
      selectedRequirementIdRef.current = connectionAddedToLaws[0].id;
      setSelectionVersion((v) => v + 1);
    }
  }, [connectionAddedToLaws.length]);

  const toggleRequirement = useCallback((requirement: any) => {
    const next = selectedRequirementIdRef.current === requirement.id ? null : requirement.id;
    selectedRequirementIdRef.current = next;
    selectedRequirementRef.current = next ? requirement : null;

    const requirementData = { id: requirement.id, name: requirement.name, number: requirement.number || '' };
    onLawCheckboxChange(requirementData, next !== null);
    setSelectionVersion((v) => v + 1);
  }, [onLawCheckboxChange]);

  useEffect(() => {
    if (companyUnits.length > 0) {
      const defaultUnit = companyUnits[0];
      // setFilters('companyUnit', [defaultUnit.name]);
    }
    setIsFiltersInitialized(true);
  }, [setFilters, companyUnits]);

  useEffect(() => {
    if (isFiltersInitialized) {
      tableRef.current && (tableRef.current as any).onQueryChange();
    }
  }, [filters, isFiltersInitialized]);

  const renderDate = (date?: Date) => {
    return date ? moment(date).format('ll') : undefined;
  };
  const handleDataFetch = useCallback(
    async (query: Query<any>) => {

      return await api.searchCustomerRequirements<any>({
        orderDirection: query.orderDirection === 'desc' ? OrderDirection.Descending : OrderDirection.Ascending,
        orderBy: query.orderBy !== undefined ? query.orderBy.field : 'name',
        page: query.page,
        pageSize: query.pageSize,
        search: query.search,
        filters: filters,
      } as IGridQueryRequest);
    },
    [api, filters, isFiltersInitialized]
  );

  const columns = useMemo(
    () => [
      {
        title: formatMessage({ id: 'law-portal.manage.law-list.table.update' }),
        field: 'select',
        width: '48px',
        sorting: false,
        removable: false,
        hiddenByColumnsButton: true,
        render: (row: any) => (
          <Checkbox
            checked={selectedRequirementIdRef.current === row.id}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleRequirement(row)}
          />
        ),
      },
      {
        title: formatMessage({ id: 'law-portal.manage.requirements.table.name' }),
        field: 'name',
        render: (row) => <RepealableName name={row.name} effectiveTo={row.effectiveTo} />,
      },
      {
        title: formatMessage({ id: 'law-portal.manage.requirements.table.description' }),
        field: 'description',
        render: (data: any) => <HtmlView variant="body2" value={data.description} />,
        sorting: false,
      },
      { title: formatMessage({ id: 'law-portal.manage.requirements.table.area' }), field: 'area' },
      {
        title: formatMessage({ id: 'law-portal.manage.requirements.table.changedDate' }),
        field: 'changedDate',
        render: (row) => renderDate(row.changedDate),
      },
      {
        title: formatMessage({ id: 'law-portal.manage.requirements.table.notes' }),
        field: 'notes',
        render: (row) => <OwnNotes {...row} linkTo={`/law-portal/other-requirements/${row.id}`} />,
        sorting: false,
      },
    ],
    [selectionVersion, formatMessage, toggleRequirement]
  );

  const handleNext = () => {
    if (connectionAddedToLaws && connectionAddedToLaws.length > 0) {
      onNext();
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <div style={{ width: '100%', padding: isMobile ? theme.spacing(2) : theme.spacing(3), backgroundColor: '#f7f0f1', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', maxHeight: '600px' }}>
        <Typography variant="body2" style={{ marginBottom: theme.spacing(2), fontSize: '15px' }}>
          {formatMessage({ id: 'law-portal.manage.law-list.update-laws.description' })}
        </Typography>
        <RequirementsListFilter filters={filters} setFilter={setFilters} />
        <Box style={{ maxHeight: '500px', overflow: 'auto', marginTop: theme.spacing(2) }}>
          {isFiltersInitialized && (
            <MTable
              tableRef={tableRef}
              columns={columns}
              data={handleDataFetch}
              components={{}}
              options={{
                pageSize: 10,
                pageSizeOptions: [5, 10, 20, 50],
                search: true,
                sorting: true,
                filtering: false,
                selection: false,
                paging: true,
                showTextRowsSelected: false,
              }}
            />
          )}
        </Box>
      </div>
      {/* <UpdateConnectionInfo
      updateConnectionInfo={updateConnectionInfo}
      currentStepName={currentStepName}
      onUpdate={setUpdateConnectionInfo}
    /> */}
      <Box>
        <StepButtonBar
          onCancel={onCancel}
          onBack={onBack}
          onNext={handleNext}
          nextLabel="form.next"
          nextDisabled={!connectionAddedToLaws || connectionAddedToLaws.length === 0}
          formatMessage={formatMessage}
          showBack={!isFirstStep}
        />
      </Box>
    </div>
  );
};

export default Step1LawsTableWithFilter;
